import { Link, Outlet, useLocation } from "react-router-dom";
import { useLang } from "../lib/useLang";
import { useCart } from "../state/CartContext";
import { isBackendConfigured } from "../lib/catalog";
import { BRANCHES_SEED } from "../data/branches";
import { StickyCartBar } from "./StickyCartBar";

function LangToggle() {
  const { lang, toggle } = useLang();
  return (
    <button
      onClick={toggle}
      className="rounded-full border border-white/30 px-3 py-1.5 text-sm font-semibold text-white/90 hover:bg-white/10"
      aria-label="toggle language"
    >
      {lang === "ar" ? "English" : "العربية"}
    </button>
  );
}

export default function Layout() {
  const { t, pick } = useLang();
  const { count } = useCart();
  const loc = useLocation();

  return (
    <div className="flex min-h-full flex-col">
      {!isBackendConfigured && (
        <div className="bg-gold/90 px-3 py-1 text-center text-xs font-semibold text-black">
          {t("demo.banner")}
        </div>
      )}

      <header className="sticky top-0 z-40 bg-brand text-white shadow-soft">
        <div className="mx-auto flex h-14 max-w-3xl lg:max-w-6xl items-center justify-between gap-3 px-4">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.svg"
              alt="Khan Baba"
              className="h-9 w-9 rounded-full ring-1 ring-white/30"
            />
            <span className="display text-xl">{t("app.name")}</span>
          </Link>
          <div className="flex items-center gap-1.5">
            <Link
              to="/track"
              className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-sm font-semibold hover:bg-white/10"
              aria-label={t("track.find")}
            >
              <span className="text-lg">🛵</span>
              <span className="hidden md:inline">{t("track.find")}</span>
            </Link>
            <LangToggle />
            <Link
              to="/account"
              className="rounded-full p-2 hover:bg-white/10"
              aria-label={t("account.title")}
            >
              <span className="text-lg">👤</span>
            </Link>
            <Link
              to="/cart"
              className="relative rounded-full p-2 hover:bg-white/10"
              aria-label={t("nav.cart")}
            >
              <span className="text-lg">🛒</span>
              {count > 0 && (
                <span className="absolute -end-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 text-[11px] font-bold text-black">
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>
        <div className="h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
      </header>

      <main className="mx-auto w-full max-w-3xl lg:max-w-6xl flex-1 px-4 pb-24 pt-2">
        <Outlet />
      </main>

      <footer className="mt-10 bg-brand-dark text-white/80">
        <div className="h-1 bg-gradient-to-r from-transparent via-gold to-transparent" />
        <div className="mx-auto max-w-3xl px-4 py-9 lg:max-w-6xl">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <img
              src="/logo.svg"
              alt="Khan Baba"
              className="h-12 w-12 rounded-full ring-1 ring-gold/40"
            />
            <div>
              <div className="display text-xl text-white">{t("app.name")}</div>
              <p className="text-sm text-white/55">{t("footer.tagline")}</p>
            </div>
          </div>

          {/* Branches */}
          <div className="mt-7 grid gap-4 sm:grid-cols-2">
            {BRANCHES_SEED.map((b) => {
              const wa = `https://wa.me/${b.whatsapp.replace(/[^0-9]/g, "")}`;
              const maps = `https://www.google.com/maps/search/?api=1&query=${b.lat},${b.lng}`;
              return (
                <div key={b.id} className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
                  <div className="display text-lg text-white">{pick(b.name, b.nameAr)}</div>
                  <p className="mt-1 text-sm leading-relaxed text-white/55">
                    {pick(b.addressEn ?? "", b.addressAr ?? "")}
                  </p>
                  <p className="mt-1.5 text-xs font-semibold text-gold">
                    🕒 {pick(b.hours ?? "", b.hoursAr ?? "")}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a href={`tel:${b.phone}`} className="chip bg-white/10 hover:bg-white/20">
                      📞 {t("footer.call")}
                    </a>
                    <a
                      href={wa}
                      target="_blank"
                      rel="noreferrer"
                      className="chip bg-accent/80 text-white hover:bg-accent"
                    >
                      💬 {t("home.whatsapp")}
                    </a>
                    <a
                      href={maps}
                      target="_blank"
                      rel="noreferrer"
                      className="chip bg-white/10 hover:bg-white/20"
                    >
                      📍 {t("footer.directions")}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/45">
          © {t("app.name")} — Kuwait ·{" "}
          <Link to="/track" className="underline hover:text-white">
            {t("track.find")}
          </Link>{" "}
          ·{" "}
          <Link to="/admin" className="underline hover:text-white">
            {t("nav.admin")}
          </Link>
        </div>
      </footer>

      <StickyCartBar key={loc.pathname} />
    </div>
  );
}
